import { supabase } from '../../providers/supabase/client';

/**
 * Fetch paginated audit logs with server-side filtering.
 *
 * @param {Object} options
 * @param {number} [options.page=1] - 1-indexed page number
 * @param {number} [options.pageSize=25] - Number of records per page
 * @param {string} [options.entityType] - Filter by entity_type (e.g. 'reservations', 'clients')
 * @param {string} [options.action] - Filter by action (e.g. 'INSERT', 'UPDATE', 'DELETE', 'LOGIN')
 * @param {string} [options.userEmail] - Filter by user email
 * @param {string} [options.dateFrom] - Start date (YYYY-MM-DD)
 * @param {string} [options.dateTo] - End date (YYYY-MM-DD)
 * @param {string} [options.search] - Free text search across user, action, entity
 * @returns {Promise<{ data: Array, count: number, error: any }>}
 */
export const fetchAuditLogs = async ({
  page = 1,
  pageSize = 25,
  entityType = 'ALL',
  action = 'ALL',
  userEmail = '',
  dateFrom = '',
  dateTo = '',
  search = '',
} = {}) => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    // Filter by entity type
    if (entityType && entityType !== 'ALL') {
      query = query.eq('entity_type', entityType);
    }

    // Filter by action
    if (action && action !== 'ALL') {
      query = query.eq('action', action);
    }

    // Filter by user email
    if (userEmail && userEmail.trim()) {
      query = query.ilike('user_email', `%${userEmail.trim()}%`);
    }

    // Date range filter
    if (dateFrom) {
      query = query.gte('created_at', `${dateFrom}T00:00:00.000Z`);
    }
    if (dateTo) {
      query = query.lte('created_at', `${dateTo}T23:59:59.999Z`);
    }

    // Search query
    if (search && search.trim()) {
      const term = search.trim();
      query = query.or(
        `user_email.ilike.%${term}%,action.ilike.%${term}%,entity_type.ilike.%${term}%,entity_id.ilike.%${term}%,ip_address.ilike.%${term}%`
      );
    }

    // Apply pagination range
    query = query.range(from, to);

    const { data, count, error } = await query;
    return { data: data || [], count: count || 0, error };
  } catch (err) {
    console.error('fetchAuditLogs error:', err);
    return { data: [], count: 0, error: err };
  }
};

/**
 * Record a custom application-level audit event via RPC.
 * Captures user IP, User-Agent, and auth.uid automatically on the database side.
 *
 * @param {Object} event
 * @param {string} event.action - e.g. 'LOGIN', 'LOGOUT', 'EXPORT_DATA', 'IMPERSONATE_START'
 * @param {string} [event.entityType='system'] - e.g. 'auth', 'report', 'system'
 * @param {string} [event.entityId] - Target ID if applicable
 * @param {string} [event.fieldName] - Modified field name if applicable
 * @param {string} [event.oldValue] - Previous value string
 * @param {string} [event.newValue] - New value string
 * @param {Object} [event.metadata] - Extra contextual JSON metadata
 * @returns {Promise<{ data: string|null, error: any }>}
 */
export const logAuditEvent = async ({
  action,
  entityType = 'system',
  entityId = null,
  fieldName = null,
  oldValue = null,
  newValue = null,
  metadata = {},
} = {}) => {
  try {
    if (!supabase || typeof supabase.rpc !== 'function') {
      return { data: null, error: null };
    }

    const { data, error } = await supabase.rpc('insert_audit_log', {
      p_action: action,
      p_entity_type: entityType,
      p_entity_id: entityId ? String(entityId) : null,
      p_field_name: fieldName,
      p_old_value: oldValue,
      p_new_value: newValue,
      p_metadata: metadata || {},
    });

    if (error) {
      console.warn('logAuditEvent RPC error:', error);
    }
    return { data, error };
  } catch (err) {
    console.warn('logAuditEvent failed silently:', err);
    return { data: null, error: err };
  }
};

/**
 * Fetch audit history for a specific entity record (e.g. all updates to a single reservation).
 *
 * @param {string} entityType - e.g. 'reservations', 'clients'
 * @param {string} entityId - Primary key value
 * @returns {Promise<{ data: Array, error: any }>}
 */
export const fetchEntityAuditLogs = async (entityType, entityId) => {
  try {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', String(entityId))
      .order('created_at', { ascending: false });

    return { data: data || [], error };
  } catch (err) {
    console.error('fetchEntityAuditLogs error:', err);
    return { data: [], error: err };
  }
};

/**
 * Fetch high-level stats for the audit log dashboard.
 */
export const fetchAuditStats = async () => {
  try {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayIso = startOfToday.toISOString();

    const [totalRes, todayRes, deletesRes] = await Promise.all([
      supabase.from('audit_logs').select('id', { count: 'exact', head: true }),
      supabase.from('audit_logs').select('id', { count: 'exact', head: true }).gte('created_at', todayIso),
      supabase.from('audit_logs').select('id', { count: 'exact', head: true }).eq('action', 'DELETE'),
    ]);

    return {
      total: totalRes.count || 0,
      today: todayRes.count || 0,
      deletes: deletesRes.count || 0,
    };
  } catch (err) {
    console.error('fetchAuditStats error:', err);
    return { total: 0, today: 0, deletes: 0 };
  }
};
